<template>
  <v-container fluid class="customer-classes">
    <div>
      <page-skeleton v-if="loading" variant="cards" class="mt-4" />
      <div dense class="mt-9" v-else>
        <open-request-notice
          :request="openRequest"
          @canceled="refreshOpenRequest"
        />
        <div v-if="needsMatch" class="match">
          <header class="match__head">
            <div class="match__eyebrow">
              {{ $t("customerClasses.eyebrow") }}
            </div>
            <h1 class="match__title">{{ $t("customerClasses.title") }}</h1>
            <p class="match__sub">{{ subText }}</p>
          </header>

          <!-- Sem disponibilidade cadastrada o match não tem o que cruzar e a
               lista vem sempre vazia. Card 86ak2ng4m: o aviso vale para
               QUALQUER aluno, não só o Autonomy — antes quem se cadastrou "com
               professor" e não informou horário caía no "nenhuma opção
               disponível" genérico e não tinha como descobrir a causa. -->
          <div v-if="needsAvailability" class="autonomy">
            <div v-if="isAutonomy" class="autonomy__badge">
              {{ $t("customerClasses.autonomyBadge") }}
            </div>
            <h2 class="autonomy__title">
              {{ $t("customerClasses.noAvailabilityTitle") }}
            </h2>
            <p class="autonomy__text">
              <template v-if="isAutonomy">
                {{ $t("customerClasses.autonomyText") }}
              </template>
              <template v-else>
                {{ $t("customerClasses.genericText") }}
              </template>
            </p>
            <button
              type="button"
              class="autonomy__btn"
              @click="goToAvailability"
            >
              <v-icon size="18" color="#fff" class="mr-2"
                >mdi-calendar-clock-outline</v-icon
              >
              {{ $t("customerClasses.addAvailability") }}
            </button>
          </div>

          <!-- Sem disponibilidade os filtros não têm o que filtrar. -->
          <div v-if="!needsAvailability" class="match__filters" role="search">
            <label class="match__search">
              <v-icon size="20" color="#9b93a3">mdi-magnify</v-icon>
              <input
                v-model="filters.query"
                type="text"
                class="match__search-input"
                :placeholder="$t('customerClasses.searchPlaceholder')"
              />
            </label>

            <div class="match__selects">
              <v-select
                v-model="filters.modality"
                :items="modalityOptions"
                :label="$t('customerClasses.modality')"
                class="match__select"
                color="#E8276F"
                item-color="#E8276F"
                outlined
                dense
                hide-details
                clearable
              />
              <v-select
                v-model="filters.frequency"
                :items="frequencyOptions"
                :label="$t('customerClasses.frequency')"
                class="match__select"
                color="#E8276F"
                item-color="#E8276F"
                outlined
                dense
                hide-details
                clearable
              />
              <v-select
                v-model="filters.weekDay"
                :items="weekDayOptions"
                :label="$t('customerClasses.day')"
                class="match__select"
                color="#E8276F"
                item-color="#E8276F"
                outlined
                dense
                hide-details
                clearable
              />
            </div>

            <div class="match__budget">
              <div class="match__budget-head">
                <span class="match__budget-label">{{
                  $t("customerClasses.budget")
                }}</span>
                <span class="match__budget-value">{{ budgetLabel }}</span>
              </div>
              <v-range-slider
                v-model="budgetRange"
                :min="budgetFloor"
                :max="budgetCeil"
                :step="budgetStep"
                color="#E8276F"
                track-color="var(--dk-line, #e7dfe6)"
                hide-details
                dense
                class="match__budget-slider"
              />
            </div>
          </div>

          <v-progress-linear
            v-if="matchLoading"
            indeterminate
            rounded
            height="3"
            color="#E8276F"
            class="mb-2"
          />

          <section v-if="matchingTeachers.length" class="match__section">
            <div class="match__section-eyebrow">
              {{ $t("customerClasses.teachersEyebrow") }}
            </div>
            <h2 class="match__section-title">
              {{ $t("customerClasses.teachersTitle") }}
            </h2>
            <p class="match__section-sub">
              {{ $t("customerClasses.teachersSub") }}
            </p>
            <div class="match__grid">
              <MatchedPartnerCard
                v-for="(teacher, subIndex) in matchingTeachers"
                :key="`teacher-${subIndex}`"
                :item="teacher"
                :limit="limit"
                :has-pending-trial="hasPendingTrial"
                :trial-done="trialTeacherIds.includes(teacher.id)"
                :blocked="!!openRequest"
                @requestJoin="sendRequestToTeacher"
              />
            </div>
            <v-pagination
              v-if="teachersPageCount > 1"
              :length="teachersPageCount"
              :value="teachersPage"
              color="#E8276F"
              circle
              class="match__pagination"
              @input="onTeachersPage"
            />
          </section>

          <section v-if="availableGroups.length" class="match__section">
            <div class="match__section-eyebrow">
              {{ $t("customerClasses.groupsEyebrow") }}
            </div>
            <h2 class="match__section-title">
              {{ $t("customerClasses.groupsTitle") }}
            </h2>
            <p class="match__section-sub">
              {{ $t("customerClasses.groupsSub") }}
            </p>
            <div class="match__grid">
              <MatchedGroupCard
                v-for="(group, index) in availableGroups"
                :key="`group-${index}`"
                :item="group"
                :student-format="profileFormatGoal"
                :student-frequency="profilePerWeek"
                :blocked="!!openRequest"
                @requestJoin="sendRequestToGroup"
              />
            </div>
            <v-pagination
              v-if="groupsPageCount > 1"
              :length="groupsPageCount"
              :value="groupsPage"
              color="#E8276F"
              circle
              class="match__pagination"
              @input="onGroupsPage"
            />
          </section>

          <div
            v-if="
              !needsAvailability &&
              !matchingTeachers.length &&
              !availableGroups.length
            "
            class="match__empty"
          >
            <v-icon size="34" color="#E8276F"
              >mdi-account-search-outline</v-icon
            >
            <div class="match__empty-title">
              {{ $t("customerClasses.emptyTitle") }}
            </div>
            <div class="match__empty-text">
              {{ $t("customerClasses.emptyText") }}
            </div>
          </div>
        </div>
        <div v-else class="joined">
          <header class="joined__head">
            <div>
              <div class="joined__eyebrow">
                {{ $t("customerClasses.myClasses") }}
              </div>
              <h1 class="joined__title">
                {{
                  data.length === 1
                    ? $t("customerClasses.yourClass")
                    : $t("customerClasses.yourClasses")
                }}
              </h1>
              <p class="joined__sub">
                {{ headerSubtitle }}
              </p>
            </div>
            <span class="joined__count">{{ data.length }}</span>
          </header>

          <div class="joined__list">
            <ClassView
              v-for="(item, index) in data"
              :key="item.id || index"
              :item="item"
            />
          </div>

          <!-- Card 86ajhjzyc: o controle de presença em conversação vive
               aqui, junto das turmas, para quem tem professor. No perfil ele
               ficava longe do resto da vida escolar do aluno. -->
          <div class="joined__conv">
            <conversation-attendance />
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import ClassView from "@/components/connect/customer/ClassView.vue";
import ConversationAttendance from "@/components/connect/customer/ConversationAttendance.vue";
import MatchedPartnerCard from "@/components/connect/partner/MatchedPartnerCard.vue";
import MatchedGroupCard from "@/components/connect/customer/MatchedGroupCard.vue";
import OpenRequestNotice from "@/components/connect/customer/OpenRequestNotice.vue";
import {
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
  WEEK_DAY_ITEMS,
} from "@/shared/classPresentation";
import { languageTag } from "@/plugins/i18n";

export default {
  name: "CustomerClasses",
  components: {
    MatchedPartnerCard,
    MatchedGroupCard,
    OpenRequestNotice,
    ClassView,
    ConversationAttendance,
  },
  async created() {
    await this.loadData();
  },
  data() {
    return {
      limit: 0,
      // O aluno ja tem uma experimental em aberto (card 86aje36nz).
      hasPendingTrial: false,
      trialTeacherIds: [],
      // Solicitação aguardando resposta (card 86akp2bkg): enquanto houver
      // uma, os pedidos ficam desabilitados e o aviso oferece o cancelamento.
      openRequest: null,
      needsMatch: false,
      count: 0,
      data: [],
      loading: true,
      // preference 1 = Autonomy (estuda sem professor); aviability = horários
      // que o aluno cadastrou no perfil.
      profilePreference: null,
      // O que o aluno pediu no cadastro, para o card da turma apontar a
      // divergência antes do pedido (card 86akj7x9f).
      profileFormatGoal: null,
      profilePerWeek: null,
      profileLoaded: false,
      availabilityCount: 0,
      matchingTeachers: [],
      availableGroups: [],
      filters: {
        query: "",
        modality: null,
        frequency: null,
        weekDay: null,
        // Faixa de valor mensal (R$). Nos extremos = sem restricao.
        valueMin: 0,
        valueMax: 1000,
      },
      // Limites do range de valor. valueMin no piso e valueMax no teto
      // significam "sem limite" e nao sao enviados ao backend.
      budgetFloor: 0,
      budgetCeil: 1000,
      budgetStep: 25,
      modalityOptions: GROUP_FORMAT_OPTIONS,
      frequencyOptions: GROUP_FREQUENCY_OPTIONS.filter((option) =>
        [1, 2].includes(option.value)
      ),
      weekDayOptions: WEEK_DAY_ITEMS,
      // Paginacao do match (server-side).
      pageSize: 6,
      teachersPage: 1,
      groupsPage: 1,
      teachersTotal: 0,
      groupsTotal: 0,
      matchLoading: false,
      filterTimer: null,
    };
  },
  computed: {
    // O match cruza a disponibilidade do aluno com a do professor. Sem horário
    // cadastrado a lista vem vazia sempre, e o aviso genérico não diz por quê.
    isAutonomy() {
      return this.profilePreference === 1;
    },
    needsAvailability() {
      return this.profileLoaded && this.availabilityCount === 0;
    },
    subText() {
      // Sem horário cadastrado não há o que escolher; o texto não pode pedir
      // uma ação que o bloco logo abaixo diz que ainda não é possível.
      if (this.needsAvailability) {
        return this.$t("customerClasses.subNoAvailability");
      }
      return this.$t("customerClasses.subDefault");
    },
    headerSubtitle() {
      if (!this.data.length) {
        return this.$t("customerClasses.headerEmpty");
      }

      return this.data.length === 1
        ? this.$t("customerClasses.headerOne")
        : this.$t("customerClasses.headerMany");
    },
    teachersPageCount() {
      return Math.max(1, Math.ceil(this.teachersTotal / this.pageSize));
    },
    groupsPageCount() {
      return Math.max(1, Math.ceil(this.groupsTotal / this.pageSize));
    },
    matchParams() {
      return {
        q: this.filters.query ? this.filters.query.trim() : undefined,
        format: this.filters.modality ?? undefined,
        frequency: this.filters.frequency ?? undefined,
        weekDay: this.filters.weekDay ?? undefined,
        // Extremos = sem limite; envia apenas quando o aluno estreita a faixa.
        valueMin:
          this.filters.valueMin > this.budgetFloor
            ? this.filters.valueMin
            : undefined,
        valueMax:
          this.filters.valueMax < this.budgetCeil
            ? this.filters.valueMax
            : undefined,
        teachersPage: this.teachersPage,
        groupsPage: this.groupsPage,
        pageSize: this.pageSize,
      };
    },
    budgetRange: {
      get() {
        return [this.filters.valueMin, this.filters.valueMax];
      },
      set([min, max]) {
        this.filters.valueMin = min;
        this.filters.valueMax = max;
      },
    },
    budgetLabel() {
      const max =
        this.filters.valueMax >= this.budgetCeil
          ? `${this.formatBudget(this.budgetCeil)}+`
          : this.formatBudget(this.filters.valueMax);
      return `${this.formatBudget(this.filters.valueMin)} – ${max}`;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.onFiltersChanged();
      },
    },
  },
  methods: {
    formatBudget(value) {
      return new Intl.NumberFormat(languageTag(), {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    goToAvailability() {
      this.$router.push({
        path: "/connect/aluno/perfil",
        query: { aba: "tab-2" },
      });
    },
    // Perfil só para saber se é Autonomy e se há horário cadastrado. Falha
    // silenciosa: sem isso, a tela apenas volta ao aviso genérico.
    async loadProfileContext() {
      try {
        const res = await customerApi.showProfile();
        this.profilePreference =
          res.data?.preference != null ? Number(res.data.preference) : null;
        this.profileFormatGoal =
          res.data?.formatGoal != null ? Number(res.data.formatGoal) : null;
        this.profilePerWeek =
          res.data?.perWeek != null ? Number(res.data.perWeek) : null;
        this.availabilityCount = Array.isArray(res.data?.aviability)
          ? res.data.aviability.length
          : 0;
        this.profileLoaded = true;
      } catch (error) {
        // Perfil nao lido nao e perfil sem horario: acusar "falta
        // disponibilidade" por causa de uma falha de rede mandaria o aluno
        // preencher algo que ele ja preencheu.
        this.profileLoaded = false;
        this.profilePreference = null;
        this.availabilityCount = 0;
      }
    },
    // Card 86akp2bkg. Falha aqui não derruba a página: a API recusa o
    // segundo pedido de qualquer forma, com a mesma explicação.
    async loadOpenRequest() {
      try {
        const res = await customerApi.openRequest();
        this.openRequest = res.data?.data || null;
      } catch (error) {
        this.openRequest = null;
      }
    },
    async refreshOpenRequest() {
      await this.loadOpenRequest();
      if (this.needsMatch && !this.needsAvailability) {
        await this.loadMatchOptions();
      }
    },
    async loadData() {
      this.loading = true;

      await this.loadProfileContext();
      await this.loadOpenRequest();

      await customerApi
        .groups()
        .then(async (groups) => {
          if (groups.data.count === 0) {
            this.needsMatch = true;
            if (!this.needsAvailability) await this.loadMatchOptions();
          } else {
            this.data = groups.data.data.data;
            this.count = groups.data.count;
          }
          this.loading = false;
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("customerClasses.loadFailed"),
            type: 1,
          });
          this.loading = false;
        });
    },
    async loadMatchOptions() {
      this.matchLoading = true;
      await customerApi
        .doAMatch(this.matchParams)
        .then((res) => {
          this.matchingTeachers = Array.isArray(res.data.teachers)
            ? res.data.teachers
            : [];
          this.availableGroups = Array.isArray(res.data.groups)
            ? res.data.groups
            : [];
          this.teachersTotal = Number(res.data.teachersTotal || 0);
          this.groupsTotal = Number(res.data.groupsTotal || 0);
          this.limit = res.data.limit;
          this.hasPendingTrial = !!res.data.hasPendingTrial;
          this.trialTeacherIds = Array.isArray(res.data.trialTeacherIds)
            ? res.data.trialTeacherIds
            : [];
        })
        .catch(() => {
          this.matchingTeachers = [];
          this.availableGroups = [];
          this.teachersTotal = 0;
          this.groupsTotal = 0;
          this.$root.$emit("display-alert", {
            text: this.$t("customerClasses.matchFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.matchLoading = false;
        });
    },
    onFiltersChanged() {
      // Debounce: filtros mudam a busca e voltam para a 1a pagina.
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.teachersPage = 1;
        this.groupsPage = 1;
        this.loadMatchOptions();
      }, 350);
    },
    onTeachersPage(page) {
      this.teachersPage = page;
      this.loadMatchOptions();
    },
    onGroupsPage(page) {
      this.groupsPage = page;
      this.loadMatchOptions();
    },
    async sendRequestToTeacher(item) {
      await customerApi
        .requestJoin({
          partnerId: item.id,
          hours: item.hours,
          experimental: !!item.experimental,
        })
        .then(async (res) => {
          this.$root.$emit("display-alert", {
            text: res.data.message + " 😊",
            type: 0,
          });
          // O aviso da solicitação em aberto aparece na hora (86akp2bkg).
          await this.refreshOpenRequest();
        })
        .catch((err) => {
          if (err.response.data.statusCode === 400) {
            this.$root.$emit("display-alert", {
              text: err.response.data.message,
              type: 1,
            });
          } else
            this.$root.$emit("display-alert", {
              text: this.$t("customerClasses.joinTeacherFailed"),
              type: 1,
            });
        });
    },
    async sendRequestToGroup(item) {
      if (
        !window.confirm(
          this.$t("customerClasses.confirmGroup", { code: item.cod })
        )
      ) {
        return;
      }

      await customerApi
        .requestJoin({ classId: item.id })
        .then((res) => {
          this.$root.$emit("display-alert", {
            text: res.data.message + " 😊",
            type: 0,
          });
          this.loadData();
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("customerClasses.joinGroupFailed"),
            type: 1,
          });
        });
    },
  },
};
</script>

<style lang="css" scoped>
.customer-classes {
  width: 100%;
}

.header {
  font-family: "Poppins", "Sans-serif";
  font-size: 27.6px;
  font-weight: bold;
}

.regular {
  font-family: "Poppins", "Sans-serif";
  font-size: 16px;
}

.normal {
  text-transform: none;
}

.section-heading {
  font-family: "Poppins", "Sans-serif";
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--dk-ink, #1f2430);
}

.section-subtitle {
  margin-top: 0.5rem;
  color: rgba(31, 36, 48, 0.7);
}

/* ---------- Estado "encontrar turma" (match) ---------- */
.match {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  padding-top: 8px;
  color: var(--ink);
}

.match__head {
  margin: 12px 0 20px;
}
.match__eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pink);
}
.match__title {
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.match__sub {
  margin: 8px 0 0;
  max-width: 60ch;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.5;
}

/* Filtros */
.match__filters {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 26px;
}
.match__search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 240px;
  min-width: 200px;
  padding: 0 14px;
  height: 44px;
  border-radius: 12px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.match__search-input {
  flex: 1 1 auto;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: var(--ink);
}
.match__search-input::placeholder {
  color: var(--dk-faint, #9b93a3);
}
.match__selects {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.match__select {
  width: 168px;
}
.match__select >>> .v-input__slot {
  border-radius: 12px !important;
  background: var(--dk-surface, #fff);
  min-height: 44px;
}

/* Range de valor mensal */
.match__budget {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  flex: 1 1 240px;
  min-width: 220px;
  padding: 4px 16px 0;
  height: 44px;
  border-radius: 12px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.match__budget-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.match__budget-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--dk-faint, #9b93a3);
}
.match__budget-value {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.match__budget-slider {
  margin: -6px 0 0;
  padding: 0;
}
.match__budget-slider >>> .v-slider {
  margin: 0;
}
.match__budget-slider >>> .v-input__slot {
  margin-bottom: 0;
}

/* Secoes */
.match__section {
  margin-top: 30px;
}
.match__section-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--pink);
}
.match__section-title {
  margin: 2px 0 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--ink);
  line-height: 1.2;
}
.match__section-sub {
  margin: 6px 0 0;
  max-width: 64ch;
  font-size: 0.88rem;
  color: var(--muted);
  line-height: 1.5;
}

/* Grid unico (mesma largura p/ professores e turmas) */
.match__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
  margin-top: 18px;
}

.match__pagination {
  margin-top: 20px;
}
.match__pagination >>> .v-pagination__item--active {
  color: #fff;
}

/* Vazio */
/* Aluno Autonomy sem disponibilidade: não é um "vazio", é uma pendência com
   uma ação clara. Por isso tem selo, cor e botão, e não o cartão neutro. */
.autonomy {
  margin-top: 28px;
  padding: 28px 26px;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(232, 39, 111, 0.22);
  border-left: 4px solid var(--pink);
  border-radius: 15px;
}
.autonomy__badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 999px;
  background: rgba(232, 39, 111, 0.1);
  color: var(--pink);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.autonomy__title {
  margin: 12px 0 0;
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.25;
  color: var(--ink);
}
.autonomy__text {
  margin: 10px 0 0;
  max-width: 62ch;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--muted);
}
.autonomy__btn {
  display: inline-flex;
  align-items: center;
  margin-top: 20px;
  padding: 12px 22px;
  border: none;
  border-radius: 12px;
  background: var(--pink);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.autonomy__btn:hover {
  filter: brightness(1.06);
}
.autonomy__btn:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .autonomy__btn {
    transition: none;
  }
}

.match__empty {
  margin-top: 40px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 15px;
}
.match__empty-title {
  margin-top: 14px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ink);
}
.match__empty-text {
  margin-top: 6px;
  max-width: 380px;
  font-size: 0.88rem;
  color: var(--muted);
  line-height: 1.55;
}

@media (max-width: 600px) {
  .match__select {
    width: 100%;
  }
  .match__selects {
    width: 100%;
  }
  .match__budget {
    flex-basis: 100%;
    width: 100%;
  }
}

/* ---------- Estado "já participa" ---------- */
.joined {
  padding-top: 8px;
}

.joined__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.joined__eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--dk-pink, #e8276f);
}

.joined__title {
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--dk-ink, #231a2e);
}

.joined__sub {
  margin: 8px 0 0;
  max-width: 52ch;
  font-size: 0.9rem;
  color: var(--dk-muted, #6b6472);
  line-height: 1.5;
}

.joined__count {
  flex: 0 0 auto;
  font-family: "Poppins", sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1;
  color: rgba(232, 39, 111, 0.22);
}

.joined__conv {
  margin-top: 28px;
}
.joined__list {
  display: grid;
  gap: 20px;
}
</style>
